import { ReactNode, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { accountSlug, billingSlug, paymentsSlug, ordersSlug } from "@utils/routes";
import {
  AccountType,
  AccountVerify,
  digitalSubscriptions,
} from "@providers/profile/ProfileProvider.model";
import { ProductModal } from "@models/product-globals.model";
import { AccountGlobalSchema } from "@models/user-account-globals";
import { StateSchema } from "./address/Address.model";
import Subscription from "./subscription";
import PaymentHistory from "./payment-history";
import OrderHistory from "./order-history";
import AccountInformation from "./account-info";
import OrderProvider from "@providers/order/OrderProvider";
import { AccountContent, AccountWrapper } from "./Account.styled";
import { useUserProfile } from "@providers/profile";

export interface AccountProps {
  verificationStatus: AccountVerify;
  accountGlobals?: AccountGlobalSchema;
  states?: Array<StateSchema>;
  membershipModal?: ProductModal;
  children: ReactNode;
}

const AccountComponent = (pathWithoutTrailingSlash, props) => {
  const { states, isDigital, accountGlobals, membershipModal, verificationStatus } = props;
  switch (pathWithoutTrailingSlash) {
    case billingSlug:
      return (
        <Subscription
          states={states}
          isDigital={isDigital}
          digitalDisclaimers={accountGlobals?.membership_disclaimers}
        />
      );
    case paymentsSlug:
      return (
        <PaymentHistory
          isDigital={isDigital}
          membershipModal={membershipModal}
          digitalDisclaimers={accountGlobals?.payment_history_disclaimers}
        />
      );
    case ordersSlug:
      return (
        <OrderHistory
          isDigital={isDigital}
          digitalDisclaimers={accountGlobals?.order_history_disclaimers}
        />
      );
    default:
      return <AccountInformation isDigital={isDigital} user={verificationStatus} />;
  }
};

const Account = ({
  verificationStatus,
  accountGlobals,
  states = [] as StateSchema[],
  membershipModal,
  children,
}: AccountProps) => {
  const { asPath, push } = useRouter();
  const [isDigital, setIsDigial] = useState<boolean>();
  const pathWithoutTrailingSlash = asPath.replace(/\/$/, "");
  const {
    profileState: { profile },
  } = useUserProfile();

  // Child accounts can only see My Account page
  useEffect(() => {
    if (
      verificationStatus?.account_type === AccountType.CHILD &&
      pathWithoutTrailingSlash !== accountSlug
    )
      push(accountSlug);
  }, [verificationStatus?.account_type, pathWithoutTrailingSlash, push]);

  useEffect(() => {
    const setDigitalStatus = () => {
      setIsDigial(digitalSubscriptions.includes(profile.subscription_tier));
    };

    if (profile.subscription_tier) setDigitalStatus();
  }, [profile]);

  return (
    <AccountWrapper className="content-page">
      <div className="grid-container">
        <div className="grid-x grid-margin-x">
          <div className="cell small-12 medium-4">{children}</div>
          <AccountContent className="cell small-12 medium-7">
            <OrderProvider>
              {AccountComponent(pathWithoutTrailingSlash, {
                states,
                isDigital,
                accountGlobals,
                membershipModal,
                verificationStatus,
              })}
            </OrderProvider>
          </AccountContent>
        </div>
      </div>
    </AccountWrapper>
  );
};
export default Account;
